import moment from 'moment';

import { areUserRolesIn } from '../../../../../helpers';
import { ClinicalRelationships, Roles } from '../../../../../constants';
import { ClinicalStatus } from '../../../../../admin/constants/general';

import type { Moment } from 'moment';
import type { IClient, IOption, IProviderAssignment } from '../../../../../utils/types';

interface GetClientOptionsParams {
  providerAssignments: IProviderAssignment[];
  clients: IClient[];
  userRoles?: Roles[];
}

export function normalizeToMinute(value: Moment | null): Moment | null {
  if (!value) return null;
  return moment(value).seconds(0).milliseconds(0);
}

export function getClientOptions({
  providerAssignments,
  clients,
  userRoles,
}: GetClientOptionsParams): IOption[] {
  if (areUserRolesIn(userRoles ?? [], [Roles.Bcba])) {
    return (
      providerAssignments
        ?.filter((assignment) => assignment.client?.clinical_status === ClinicalStatus.CLINICAL)
        ?.map((assignment) => ({
          id: assignment.client.id,
          label: `${assignment.client?.first_name} ${assignment.client?.last_name}`,
          description:
            assignment.clinical_relationship === ClinicalRelationships.HomeBcba
              ? ''
              : assignment.clinical_relationship?.split('_')[0],
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }

  return mapClientsToOptions(clients);
}

function mapClientsToOptions(clients: IClient[]): IOption[] {
  return (
    clients
      ?.map((client) => ({
        id: client.id,
        label: `${client.first_name} ${client.last_name}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []
  );
}

export function getSelectedClientZipCode(
  clients: IClient[],
  clientId?: number,
): string | number | null {
  return clients.find((client) => client.id === clientId)?.street_address_zip ?? null;
}

export function getSelectedClientLabel(clientOptions: IOption[], clientId?: number): string {
  if (clientOptions.length === 0 || !clientId) return '';
  return clientOptions.find((option) => option.id === clientId)?.label ?? '';
}

export function getTimezoneMessage(clientId?: number, isTZIdentified?: boolean): string {
  if (!clientId) return 'Select a client first';
  if (!isTZIdentified) return 'Timezone undetermined';
  return 'Select date & time first';
}

export const getClientTimeTooltip = (
  clientId: number | undefined,
  selectedClientDetails: IClient | null,
): string => {
  if (!clientId) return 'Select a client to determine their timezone';

  const zipSuffix = selectedClientDetails
    ? `(Client zip code: ${selectedClientDetails.street_address_zip})`
    : '';

  return `Client's timezone is determined by their zip code ${zipSuffix}`;
};
